import { CircleAlert } from 'lucide-react';
import { Link } from 'react-router';
import { describeError } from '../lib/errors';

/**
 * Explains a failure: what failed (message), the individual problems (blockers),
 * what changed (inferred from the status), and what to do next (hint).
 */
export function ErrorAlert({ error, title, onRetry, className = '' }: { error: unknown; title?: string; onRetry?: () => void; className?: string }) {
  if (!error) return null;
  const info = describeError(error);
  return (
    <div role="alert" className={`alert animate-rise items-start border-error/20 alert-soft alert-error ${className}`}>
      <CircleAlert className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1 text-sm">
        <p className="text-[0.9375rem] font-semibold">{title ?? info.title}</p>
        <p className="break-anywhere">{info.message}</p>
        {info.items.length > 0 ? (
          <ul className="list-disc space-y-0.5 pl-5">
            {info.items.map((b, i) => (
              <li key={i} className="break-anywhere">
                {b}
              </li>
            ))}
          </ul>
        ) : null}
        {info.hint ? (
          <p>
            <span className="font-medium">What to do next: </span>
            {info.hint}
          </p>
        ) : null}
        {info.changed ? <p className="opacity-80">{info.changed}</p> : null}
        {info.jobId ? (
          <p>
            <Link className="link" to={`/jobs/${info.jobId}`}>
              Open the related job
            </Link>
          </p>
        ) : null}
        {info.code ? (
          <p className="font-mono text-xs opacity-60">
            {info.code}
            {info.status ? ` · HTTP ${info.status}` : ''}
          </p>
        ) : null}
      </div>
      {onRetry ? (
        <button type="button" className="btn btn-sm" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}
